/**
 * Pure derivations from client_approvals payment columns.
 */

import { formatNZDCompact } from "@shared/nz-money";

export interface PaymentSummary {
  quoteTotal: number;
  depositAmount: number;
  balanceAmount: number;
  depositStatus: string;
  balanceStatus: string;
}

export interface ApprovalForSummary {
  payment_status?: string;
  balance_status?: string;
  paid_at?: string | null;
  balance_paid_at?: string | null;
  client_confirmed_deposit_at?: string | null;
  client_confirmed_balance_at?: string | null;
  balance_invoiced_at?: string | null;
  sent_to_client_at?: string | null;
  deposit_reminder_sent_at?: string | null;
  balance_reminder_sent_at?: string | null;
}

export interface PaymentDerivation {
  depositDone: boolean;
  balanceDone: boolean;
  balanceInvoiceSent: boolean;
  collapsedSummary: string;
}

/**
 * The one answer to "is the deposit settled?" — a $0 deposit counts as done.
 * deriveWorkspaceView (booking-phase.ts) imports these instead of re-deriving;
 * this file is the single payments derivation.
 */
export function isDepositDone(
  depositStatus: string,
  depositAmount: number,
): boolean {
  return depositStatus === "paid" || depositAmount === 0;
}

/** The one answer to "is the balance settled?". */
export function isBalanceDone(balanceStatus: string): boolean {
  return balanceStatus === "paid";
}

export function derivePaymentSummary(
  paymentSummary: PaymentSummary,
  approval: ApprovalForSummary | null,
): PaymentDerivation {
  const { depositStatus, balanceStatus, depositAmount } = paymentSummary;

  const depositDone = isDepositDone(depositStatus, depositAmount);
  const balanceDone = isBalanceDone(balanceStatus);

  const balanceInvoiceSent =
    balanceStatus === "invoiced" ||
    balanceStatus === "client_confirmed" ||
    balanceStatus === "paid" ||
    !!approval?.balance_invoiced_at;

  const collapsedSummary = buildCollapsedSummary(
    paymentSummary,
    depositDone,
    balanceDone,
  );

  return { depositDone, balanceDone, balanceInvoiceSent, collapsedSummary };
}

function buildCollapsedSummary(
  s: PaymentSummary,
  depositDone: boolean,
  balanceDone: boolean,
): string {
  if (depositDone && balanceDone) return "Fully paid";
  if (s.balanceStatus === "paid") return "Balance paid";
  if (s.balanceStatus === "client_confirmed") {
    return `Balance: client says paid, verify ${formatNZDCompact(s.balanceAmount)}`;
  }
  if (s.balanceStatus === "invoiced") {
    return `Balance invoiced, awaiting ${formatNZDCompact(s.balanceAmount)}`;
  }
  if (depositDone) {
    return s.depositAmount > 0
      ? `Deposit paid, send balance invoice (${formatNZDCompact(s.balanceAmount)} remaining)`
      : `${formatNZDCompact(s.balanceAmount)} due`;
  }
  if (s.depositStatus === "client_confirmed") {
    return "Deposit: client says paid, verify in bank";
  }
  if (s.depositAmount === 0) return `${formatNZDCompact(s.balanceAmount)} due`;
  return `Waiting for ${formatNZDCompact(s.depositAmount)} deposit`;
}

export interface PaymentActions {
  sendDepositReminder: boolean;
  sendBalanceInvoice: boolean;
  sendBalanceInvoiceLabel: string;
  sendBalanceReminder: boolean;
  confirmDepositReceived: boolean;
  confirmBalanceReceived: boolean;
}

export interface PaymentActionOptions {
  balanceInvoiceNeedsReissue?: boolean;
}

export function derivePaymentActions(
  paymentSummary: PaymentSummary,
  approval: ApprovalForSummary | null,
  options: PaymentActionOptions = {},
): PaymentActions {
  const { depositStatus, balanceStatus, depositAmount } = paymentSummary;
  const { balanceInvoiceSent, depositDone } = derivePaymentSummary(
    paymentSummary,
    approval,
  );

  const sendDepositReminder =
    !balanceInvoiceSent &&
    depositStatus !== "paid" &&
    depositStatus !== "skipped" &&
    depositAmount > 0;

  // Gated on balanceInvoiceSent, not on `balanceStatus !== "invoiced"`. The
  // narrower test let a balance sitting at client_confirmed — invoiced, and
  // the Customer has since said they paid — offer the invoice a second time,
  // and MarkBalanceInvoiced claims from any non-paid state, so clicking it
  // walked the status BACK to invoiced and erased the "client says paid"
  // badge. A first send has nothing to do once the invoice has gone out.
  const sendBalanceInvoice =
    depositDone &&
    balanceStatus !== "paid" &&
    (!balanceInvoiceSent || !!options.balanceInvoiceNeedsReissue);
  const sendBalanceInvoiceLabel = options.balanceInvoiceNeedsReissue
    ? "Send Updated Balance Invoice"
    : "Send Remaining Balance Invoice";

  // The reminder is the action for any sent-but-unpaid balance, client_confirmed
  // included: the Operator is still waiting on money they can see in the bank.
  const sendBalanceReminder =
    depositDone &&
    (balanceStatus === "invoiced" || balanceStatus === "client_confirmed") &&
    !options.balanceInvoiceNeedsReissue;

  const confirmDepositReceived =
    depositAmount > 0 &&
    !approval?.paid_at &&
    !!(approval?.client_confirmed_deposit_at || approval?.sent_to_client_at);

  const confirmBalanceReceived =
    depositDone &&
    !approval?.balance_paid_at &&
    (balanceStatus === "invoiced" || balanceStatus === "client_confirmed") &&
    !!(approval?.client_confirmed_balance_at || approval?.balance_invoiced_at);

  return {
    sendDepositReminder,
    sendBalanceInvoice,
    sendBalanceInvoiceLabel,
    sendBalanceReminder,
    confirmDepositReceived,
    confirmBalanceReceived,
  };
}
